import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { enqueueSnackbar } from 'notistack';
import { runEntityRiskEvaluation } from 'api/bitedb/entities/entityRisk.service';
import {
  responseRiskEvaluation,
  RiskEvaluation,
  storedRiskEvaluation,
} from './entityRiskEvaluation';

export type RiskRunState = 'idle' | 'running' | 'done';

export interface EntityRiskRun {
  state: RiskRunState;
  evaluation: RiskEvaluation | null;
  startedAt: number | null;
  /** The evaluation came from a run in this visit, not from the record. */
  finishedHere: boolean;
  error: string | null;
  /** The server filed this visit's run to the record's Library. */
  libraryFiled: boolean;
  run: () => Promise<void>;
}

interface EntityRiskRecord {
  record: Record<string, any> | null | undefined;
  /** Changes when the page shows another entity; a late answer is dropped. */
  recordKey: string;
  name?: string;
  country?: string;
  address?: string;
  statedBusiness?: string;
  aliases?: string[];
  databaseId?: string | number | null;
  databaseEntityId?: string | number | null;
  entityId?: string | number | null;
  /** Reloads the record once the agents have saved their results. */
  onSaved?: () => void;
  /** Reloads the Library once the server has filed a run's report to it. */
  onReportFiled?: () => void;
}

const text = (value: unknown) =>
  value === undefined || value === null || value === ''
    ? undefined
    : String(value);

/**
 * Runs the entity service's combined risk evaluation for one record. Each
 * agent saves its own result to the record, so the last run shows again after
 * a reload (storedRiskEvaluation), and a run the reviewer walks away from
 * still lands on the record.
 */
export const useEntityRiskEvaluation = ({
  record,
  recordKey,
  name,
  country,
  address,
  statedBusiness,
  aliases,
  databaseId,
  databaseEntityId,
  entityId,
  onSaved,
  onReportFiled,
}: EntityRiskRecord): EntityRiskRun => {
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [fresh, setFresh] = useState<RiskEvaluation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [libraryFiled, setLibraryFiled] = useState(false);
  const currentKeyRef = useRef(recordKey);
  currentKeyRef.current = recordKey;
  // Guards the run synchronously: state set on the first click is not yet
  // visible to a second click in the same frame, which would start a second
  // set of live searches.
  const runningRef = useRef(false);

  useEffect(() => {
    runningRef.current = false;
    setStartedAt(null);
    setFresh(null);
    setError(null);
    setLibraryFiled(false);
  }, [recordKey]);

  const stored = useMemo(() => storedRiskEvaluation(record), [record]);

  const run = useCallback(async () => {
    if (runningRef.current || !name) return;
    runningRef.current = true;
    const runKey = recordKey;
    setStartedAt(Date.now());
    setError(null);
    setLibraryFiled(false);
    const payload = {
      entityName: name,
      country: text(country),
      address: text(address),
      statedBusiness: text(statedBusiness),
      aliases: aliases && aliases.length ? aliases : undefined,
      entityId: text(entityId),
    };
    const recordDatabaseId = text(databaseId);
    const recordId = text(databaseEntityId);
    if (!recordDatabaseId || !recordId) {
      runningRef.current = false;
      setStartedAt(null);
      const message =
        'Export and Transshipment Risk needs a saved record. Open the entity from its database and try again.';
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
      return;
    }
    const response = await runEntityRiskEvaluation(
      recordDatabaseId,
      recordId,
      payload,
    );
    if (response && 'reportFiled' in response && response.reportFiled) {
      onReportFiled?.();
    }
    if (currentKeyRef.current !== runKey) return;
    runningRef.current = false;
    setStartedAt(null);
    if (!response?.success) {
      const message =
        'Export and Transshipment Risk could not run. Try again in a moment.';
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
      return;
    }
    setLibraryFiled('reportFiled' in response && response.reportFiled);
    const result = responseRiskEvaluation(
      response.result,
      new Date().toISOString(),
    );
    setFresh(result);
    onSaved?.();
    enqueueSnackbar(
      result.failed
        ? `Export and Transshipment Risk finished; ${result.failed} of ${result.agents.length} agents did not answer`
        : `Export and Transshipment Risk finished for ${name}`,
      { variant: result.failed ? 'warning' : 'success' },
    );
  }, [
    address,
    aliases,
    country,
    databaseEntityId,
    databaseId,
    entityId,
    name,
    onReportFiled,
    onSaved,
    recordKey,
    statedBusiness,
  ]);

  const evaluation = fresh || stored;
  return {
    state: startedAt ? 'running' : evaluation ? 'done' : 'idle',
    evaluation: evaluation,
    startedAt: startedAt,
    finishedHere: Boolean(fresh),
    error: error,
    libraryFiled: libraryFiled,
    run: run,
  };
};
